import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiRotateCcw,
  FiShoppingBag,
  FiTruck,
} from "react-icons/fi";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";

const OPEN_PO_STATUSES = [
  "DRAFT",
  "SUBMITTED",
  "APPROVED",
  "PARTIALLY_RECEIVED",
];

const PENDING_DELIVERY_STATUSES = ["APPROVED", "PARTIALLY_RECEIVED"];

const formatCurrency = (value, compact = false) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    notation: compact ? "compact" : "standard",
    maximumFractionDigits: compact ? 1 : 2,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
};

const getOrderTotal = (order) =>
  Number(
    order.grandTotal ?? order.totalAmount ?? order.total ?? order.netTotal ?? 0,
  );

const getReceiptTotal = (grn) =>
  Number(
    grn.grandTotal ??
      grn.totalAmount ??
      grn.total ??
      grn.purchaseOrder?.grandTotal ??
      0,
  );

const getReturnTotal = (supplierReturn) => {
  if (supplierReturn.commercialTotal !== undefined) {
    return Number(supplierReturn.commercialTotal || 0);
  }

  if (supplierReturn.totalAmount !== undefined) {
    return Number(supplierReturn.totalAmount || 0);
  }

  return (supplierReturn.items || []).reduce(
    (sum, item) => sum + Number(item.lineTotal || 0),
    0,
  );
};

const getSupplierName = (item) =>
  item.supplier?.displayName ||
  item.supplier?.name ||
  item.purchaseOrder?.supplier?.displayName ||
  item.purchaseOrder?.supplier?.name ||
  "Unknown Supplier";

const getStatusStyle = (status) => {
  const styles = {
    DRAFT: "bg-gray-100 text-gray-700",
    SUBMITTED: "bg-amber-100 text-amber-700",
    APPROVED: "bg-blue-100 text-blue-700",
    PARTIALLY_RECEIVED: "bg-purple-100 text-purple-700",
    RECEIVED: "bg-green-100 text-green-700",
    CLOSED: "bg-green-100 text-green-700",
    CANCELLED: "bg-red-100 text-red-700",
    POSTED: "bg-green-100 text-green-700",
    DISPATCHED: "bg-blue-100 text-blue-700",
  };

  return styles[status] || "bg-gray-100 text-gray-700";
};

const readableStatus = (status) =>
  String(status || "—")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

export default function PurchaseDashboard() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [grns, setGrns] = useState([]);
  const [supplierReturns, setSupplierReturns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadDashboard = useCallback(async () => {
    if (!accessToken) return;

    try {
      setIsLoading(true);
      setError("");

      const [ordersResponse, grnsResponse, returnsResponse] = await Promise.all(
        [
          apiRequest("/purchases/orders", { accessToken }),
          apiRequest("/purchases/grns", { accessToken }),
          apiRequest("/purchases/returns", { accessToken }),
        ],
      );

      setPurchaseOrders(
        Array.isArray(ordersResponse)
          ? ordersResponse
          : ordersResponse?.data || ordersResponse?.items || [],
      );

      setGrns(
        Array.isArray(grnsResponse)
          ? grnsResponse
          : grnsResponse?.data || grnsResponse?.items || [],
      );

      setSupplierReturns(
        Array.isArray(returnsResponse)
          ? returnsResponse
          : returnsResponse?.data || returnsResponse?.items || [],
      );
    } catch (requestError) {
      setError(
        requestError.message ||
          "Could not load the purchase dashboard. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const dashboard = useMemo(() => {
    const receivedOrders = purchaseOrders.filter((order) =>
      ["RECEIVED", "CLOSED"].includes(order.status),
    );

    const purchaseValue = receivedOrders.reduce(
      (sum, order) => sum + getOrderTotal(order),
      0,
    );

    const openOrders = purchaseOrders.filter((order) =>
      OPEN_PO_STATUSES.includes(order.status),
    );

    const pendingDeliveries = purchaseOrders.filter((order) =>
      PENDING_DELIVERY_STATUSES.includes(order.status),
    );

    const activeReturns = supplierReturns.filter(
      (item) => !["CANCELLED", "CLOSED"].includes(item.status),
    );

    const supplierMap = purchaseOrders.reduce((accumulator, order) => {
      if (["CANCELLED", "DRAFT"].includes(order.status)) {
        return accumulator;
      }

      const supplierName = getSupplierName(order);
      accumulator[supplierName] =
        (accumulator[supplierName] || 0) + getOrderTotal(order);

      return accumulator;
    }, {});

    const topSuppliers = Object.entries(supplierMap)
      .map(([name, value]) => ({ name, value }))
      .sort((first, second) => second.value - first.value)
      .slice(0, 4);

    const monthBuckets = Array.from({ length: 6 }, (_, index) => {
      const date = new Date();
      date.setMonth(date.getMonth() - (5 - index));
      date.setDate(1);

      return {
        key: `${date.getFullYear()}-${date.getMonth()}`,
        month: date.toLocaleString("en-IN", { month: "short" }),
        value: 0,
      };
    });

    purchaseOrders
      .filter((order) => !["CANCELLED", "DRAFT"].includes(order.status))
      .forEach((order) => {
        const date = new Date(order.createdAt);

        if (Number.isNaN(date.getTime())) return;

        const key = `${date.getFullYear()}-${date.getMonth()}`;
        const bucket = monthBuckets.find((item) => item.key === key);

        if (bucket) {
          bucket.value += getOrderTotal(order);
        }
      });

    return {
      purchaseValue,
      openOrders,
      pendingDeliveries,
      activeReturns,
      topSuppliers,
      purchaseTrend: monthBuckets,
      recentOrders: [...purchaseOrders]
        .sort(
          (first, second) =>
            new Date(second.createdAt) - new Date(first.createdAt),
        )
        .slice(0, 5),
      recentGrns: [...grns]
        .sort(
          (first, second) =>
            new Date(second.createdAt) - new Date(first.createdAt),
        )
        .slice(0, 5),
    };
  }, [purchaseOrders, grns, supplierReturns]);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-500">
        Loading purchase dashboard...
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <p className="text-sm text-gray-500">Purchasing</p>

        <h1 className="text-3xl font-bold text-gray-900 mt-1">
          Purchase Dashboard
        </h1>

        <p className="text-gray-500 mt-2">
          Overview of procurement operations and supplier performance.
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-100 rounded-2xl p-4 flex gap-3 text-red-700">
          <FiAlertTriangle className="mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">
              Some purchase data could not be loaded.
            </p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      <div className="grid xl:grid-cols-4 md:grid-cols-2 gap-5 mb-6">
        <StatCard
          title="Received Purchase Value"
          value={formatCurrency(dashboard.purchaseValue, true)}
          icon={FiDollarSign}
          color="green"
        />

        <StatCard
          title="Open Purchase Orders"
          value={dashboard.openOrders.length}
          icon={FiShoppingBag}
          color="blue"
        />

        <StatCard
          title="Pending Deliveries"
          value={dashboard.pendingDeliveries.length}
          icon={FiTruck}
          color="orange"
        />

        <StatCard
          title="Open Supplier Returns"
          value={dashboard.activeReturns.length}
          icon={FiRotateCcw}
          color="red"
        />
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 p-6 mb-6">
        <div className="mb-5">
          <h3 className="text-lg font-semibold text-gray-900">
            Monthly Purchase Trend
          </h3>

          <p className="text-sm text-gray-500 mt-1">
            Purchase order value over the last six months.
          </p>
        </div>

        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dashboard.purchaseTrend}>
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#6b7280", fontSize: 12 }}
              />

              <Tooltip
                formatter={(value) => formatCurrency(value)}
                contentStyle={{
                  borderRadius: "12px",
                  border: "1px solid #f3f4f6",
                }}
              />

              <Area
                type="monotone"
                dataKey="value"
                stroke="#92263f"
                strokeWidth={3}
                fill="#fde8ea"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <SectionCard
          title="Recent Purchase Orders"
          actionLabel="View All"
          onAction={() => navigate("/admindashboard/purchases/orders")}
        >
          {dashboard.recentOrders.length ? (
            dashboard.recentOrders.map((order) => (
              <button
                key={order.id}
                onClick={() =>
                  navigate(`/admindashboard/purchases/orders/${order.id}`)
                }
                className="w-full flex justify-between items-center gap-4 p-4 rounded-2xl bg-gray-50 hover:bg-primary-50 transition text-left"
              >
                <div>
                  <p className="font-semibold text-gray-900">
                    {order.purchaseOrderNumber}
                  </p>

                  <p className="text-sm text-gray-500 mt-1">
                    {getSupplierName(order)} · {formatDate(order.createdAt)}
                  </p>
                </div>

                <div className="text-right">
                  <p className="font-semibold text-gray-900">
                    {formatCurrency(getOrderTotal(order), true)}
                  </p>

                  <StatusBadge status={order.status} />
                </div>
              </button>
            ))
          ) : (
            <EmptyState text="No purchase orders yet." />
          )}
        </SectionCard>

        <SectionCard
          title="Recent GRNs"
          actionLabel="View All"
          onAction={() => navigate("/admindashboard/purchases/grn")}
        >
          {dashboard.recentGrns.length ? (
            dashboard.recentGrns.map((grn) => (
              <button
                key={grn.id}
                onClick={() =>
                  navigate(`/admindashboard/purchases/grn/${grn.id}`)
                }
                className="w-full flex justify-between items-center gap-4 p-4 rounded-2xl bg-gray-50 hover:bg-primary-50 transition text-left"
              >
                <div>
                  <p className="font-semibold text-gray-900">
                    {grn.goodsReceiptNumber}
                  </p>

                  <p className="text-sm text-gray-500 mt-1">
                    {getSupplierName(grn)} · {formatDate(grn.createdAt)}
                  </p>
                </div>

                <div className="text-right">
                  <p className="font-semibold text-gray-900">
                    {formatCurrency(getReceiptTotal(grn), true)}
                  </p>

                  <StatusBadge status={grn.status} />
                </div>
              </button>
            ))
          ) : (
            <EmptyState text="No GRNs have been created yet." />
          )}
        </SectionCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <SectionCard title="Top Suppliers">
          {dashboard.topSuppliers.length ? (
            dashboard.topSuppliers.map((supplier) => (
              <div
                key={supplier.name}
                className="flex justify-between items-center gap-4 p-4 rounded-2xl bg-gray-50"
              >
                <span className="font-medium text-gray-900">
                  {supplier.name}
                </span>

                <span className="font-bold text-gray-900">
                  {formatCurrency(supplier.value, true)}
                </span>
              </div>
            ))
          ) : (
            <EmptyState text="Supplier totals will appear after purchase orders are created." />
          )}
        </SectionCard>

        <SectionCard
          title="Pending Deliveries"
          actionLabel="View Orders"
          onAction={() => navigate("/admindashboard/purchases")}
        >
          {dashboard.pendingDeliveries.length ? (
            dashboard.pendingDeliveries.slice(0, 5).map((order) => (
              <button
                key={order.id}
                onClick={() =>
                  navigate(`/admindashboard/purchases/${order.id}`)
                }
                className="w-full flex justify-between items-center gap-4 p-4 rounded-2xl bg-gray-50 hover:bg-primary-50 transition text-left"
              >
                <div>
                  <p className="font-medium text-gray-900">
                    {order.purchaseOrderNumber}
                  </p>

                  <p className="text-sm text-gray-500 mt-1">
                    {getSupplierName(order)}
                  </p>
                </div>

                <span className="px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold">
                  {order.status === "PARTIALLY_RECEIVED"
                    ? "Partially Received"
                    : "Awaiting Delivery"}
                </span>
              </button>
            ))
          ) : (
            <EmptyState text="No pending deliveries." />
          )}
        </SectionCard>
      </div>
    </div>
  );
}

function SectionCard({ title, actionLabel, onAction, children }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6">
      <div className="flex items-center justify-between gap-4 mb-5">
        <h3 className="font-semibold text-lg text-gray-900">{title}</h3>

        {actionLabel && (
          <button
            onClick={onAction}
            className="text-primary-700 hover:text-primary-800 text-sm font-medium"
          >
            {actionLabel}
          </button>
        )}
      </div>

      <div className="space-y-4">{children}</div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="rounded-2xl bg-gray-50 px-5 py-8 text-center text-sm text-gray-500">
      {text}
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color }) {
  const colors = {
    green: "bg-green-50 text-green-600",
    blue: "bg-blue-50 text-blue-600",
    orange: "bg-orange-50 text-orange-600",
    red: "bg-red-50 text-red-600",
  };

  return (
    <div className="bg-white border border-gray-100 rounded-3xl p-6">
      <div className="flex justify-between items-center gap-4">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="text-3xl font-bold text-gray-900 mt-2">{value}</h3>
        </div>

        <div
          className={`h-14 w-14 shrink-0 rounded-2xl flex items-center justify-center ${colors[color]}`}
        >
          <Icon size={24} />
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex mt-2 text-xs px-2 py-1 rounded-full font-medium ${getStatusStyle(
        status,
      )}`}
    >
      {readableStatus(status)}
    </span>
  );
}
